// api/payment.js
// Shared Razorpay checkout flow, used by every buy button on the site.
import { workbooksApi } from "./workbooks";
import { isLoggedIn } from "./auth";
import { currentPath, loginUrlFor } from "../lib/nextUrl";

// Loads the Razorpay checkout script once. Resolves true when it is ready.
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// Runs the full checkout for a product code from the catalogue.
//
// No amount is sent. This used to pass a client-computed "hint" alongside the
// product, guessed from a two-key pricing response with hardcoded rupee
// fallbacks behind it. The server never needed it and now refuses to read it:
// the Products row is the only price. Passing one could only ever make the page
// disagree with the till.
//
// `title` is the product's own title, used as the Razorpay description so the
// buyer sees the thing they clicked rather than a generic label.
// Checkout ends by settling: re-read ownership from the server, then move the
// user, in that order and without reloading the document.
//
// `onSettled` re-reads ownership. It is called whenever checkout ends in a way
// that may have changed what the caller owns: a completed payment, a payment
// whose verify call failed, or a dismissed modal. It is awaited, so the
// navigation that follows happens against the server's answer rather than
// racing it.
//
// `redirectTo` is where to go once ownership is current, navigated with
// replace: the buy page must not sit in history behind the thing that was just
// bought. Omit it to stay put, which is what a page does when it corrects
// itself in place (the bundle page flipping to Owned) or when the user should
// not move at all (unlocking a section inside the reader).
//
// This used to be `window.location.href = redirectTo`: a full document reload,
// chosen so the destination refetched ownership on mount. It did refetch, but
// it also blanked every bit of React state on the way, which is how a buyer who
// unlocked a section from inside the reader came back to a different section
// than the one they had paid to read.
// `onAuthLost` is called for the one case that is neither a success nor a
// failure: the money was taken but the session went away before the app could
// confirm it. A page that passes it shows the message itself; pages that do not
// get a plain alert, because saying nothing here is the worst option.
//
// `navigate` is required, and there is no window.location fallback anywhere in
// here: reloading the document is the behaviour this function exists to remove.
// A caller without it is a programming error, so it throws in development
// rather than quietly degrading in front of a buyer.
export async function startCheckout({
  productCode,
  title,
  redirectTo,
  navigate,
  onSettled,
  onAuthLost,
}) {
  if (!navigate) {
    const message =
      "startCheckout requires `navigate`: checkout must not reload the document.";
    if (process.env.NODE_ENV !== "production") throw new Error(message);
    console.error(message);
    return;
  }

  // Re-read ownership, then move. Both steps are optional on their own: a page
  // that corrects itself in place passes no redirectTo, and a page with nothing
  // cached passes no onSettled.
  const settle = async () => {
    if (onSettled) {
      try {
        await onSettled();
      } catch (err) {
        // A failed re-read must not swallow the navigation: the destination
        // reads ownership on mount anyway.
        console.error("Ownership refresh after checkout failed:", err);
      }
    }
    if (!redirectTo) return;
    // replace: the buy page has served its purpose, and back must not return to
    // it holding a live buy button for something already owned.
    navigate(redirectTo, { replace: true });
  };
  // An order is created against the signed-in user, so checkout needs an
  // account. Send them to login before anything else happens, rather than
  // letting the order call 401 and surface as a "payment failed" alert sitting
  // on top of the redirect it was hiding.
  if (!isLoggedIn()) {
    // ?next= rather than router state, so the destination survives a refresh of
    // /login and the Google SSO round-trip. It also keeps the hash, which the
    // old pathname + search did not.
    navigate(loginUrlFor(currentPath()));
    return;
  }

  if (!productCode) {
    // Nothing is on sale for this bundle yet. A button that reaches here is a
    // bug, but a silent no-op beats opening Razorpay for an unknown product.
    console.warn("startCheckout called with no product code.");
    return;
  }

  try {
    const loaded = await loadRazorpay();
    if (!loaded) {
      alert("Failed to load payment gateway.");
      return;
    }

    const order = await workbooksApi.createOrder({ product: productCode });

    // A session that expired between page load and this click 401s here. That
    // now throws and is handled in the catch below; createOrder is marked
    // authRequired, so SessionGuard is already moving them to login. A response
    // with no order id is a different, non-401 failure: nothing to open.
    if (!order?.razorpayOrderId) return;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      // The server's number, which is the Products row price.
      amount: order.amount,
      currency: "INR",
      name: "ScriptedCAD",
      description: title || "ScriptedCAD",
      order_id: order.razorpayOrderId,
      handler: async (response) => {
        // product is not sent: the server grants what the ORDER was created
        // for, read from its own row. A client that could name the product to
        // grant could pay for the cheap one and claim the expensive one.
        try {
          await workbooksApi.verifyPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
        } catch (err) {
          // The payment itself may well have succeeded: Razorpay confirms the
          // order to the webhook independently of this call, and the webhook
          // grants. This used to throw out of an async handler that nothing
          // was awaiting, so the buyer was left sitting on the buy page with
          // their money taken and the button still live. Log it and carry on
          // to the refresh below, which asks the server what they own rather
          // than guessing from whether one request succeeded.
          console.error("Verify failed after payment:", err);
          // A 401 here is the one case that must not carry on, and must not
          // navigate either.
          //
          // The charge has already happened and Razorpay has confirmed the
          // order to the webhook, which grants independently of this call, so
          // the purchase is safe. What is gone is the session. Carrying on to
          // `onSettled` would re-read ownership with no token, get back the
          // public answer ("you own nothing"), and paint a live buy button over
          // something the buyer has just paid for. That is how you get charged
          // twice.
          //
          // So: say what happened and stop. Signing in again remounts the
          // destination page, which reads ownership from the server, and the
          // purchase is there.
          if (err.status === 401) {
            const message =
              "Payment received. Sign in to access your purchase.";
            if (onAuthLost) onAuthLost(message);
            else alert(message);
            return;
          }
        }
        await settle();
      },
      // A dismissed modal is not necessarily an abandoned payment. On mobile
      // UPI the buyer leaves for their bank app, pays, and comes back to a
      // closed sheet while the webhook does the granting. Tell the caller so
      // it can re-read ownership; if nothing paid, the re-read changes nothing.
      modal: {
        ondismiss: () => {
          settle();
        },
      },
      theme: { color: "#2563eb" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (err) {
    // 401: the session expired between page load and this click. createOrder is
    // marked authRequired, so SessionGuard is already moving them to login with
    // this page recorded as where to come back to. An alert here would sit on
    // top of that redirect and have to be dismissed before it could happen,
    // which is the bug the login check at the top of this function exists to
    // avoid in the first place.
    if (err.status === 401) return;
    // 409: the server refused because this user already owns everything the
    // product grants. That is a stale page, not a failure - it is exactly what
    // a buy button left open across a purchase looks like. Re-read ownership
    // and let the page correct itself instead of raising an alarm.
    if (err.status === 409) {
      await settle();
      return;
    }
    console.error("Payment error:", err);
    alert("Payment failed: " + err.message);
  }
}
